import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSidebar } from '../../contexts/SidebarContext';
import HeaderNavigation from '../../components/ui/HeaderNavigation';
import MainSidebar from '../../components/ui/MainSidebar';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import NotificationToast from '../../components/ui/NotificationToast';
import Button from '../../components/ui/Button';
import useKeyboardShortcuts from '../../hooks/useKeyboardShortcuts';

// Import services
import employeeService from '../../services/employeeService';
import activityLogService from '../../services/activityLogService';
import attendanceBatchService from '../../services/attendanceBatchService';

// Import components
import EmployeeStatsCards from './components/EmployeeStatsCards';
import EmployeeFilters from './components/EmployeeFilters';
import EmployeeTable from './components/EmployeeTable';
import EmployeeModal from './components/EmployeeModal';
import BulkActionsModal from './components/BulkActionsModal';
import EmployeeManagementTabs from './components/EmployeeManagementTabs';
import EmployeeImportModal from './components/EmployeeImportModal';
import ClientFilter from './components/ClientFilter';

const EmployeeManagement = () => {
  const { user, userProfile, loading: authLoading } = useAuth();
  const location = useLocation();
  const { isCollapsed } = useSidebar();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('view');
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [employeeStats, setEmployeeStats] = useState(null);
  const [resetKey, setResetKey] = useState(0);

  // Load employees data
  const loadEmployees = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);

      const requestFilters = { ...filters };
      if (selectedClientId) {
        requestFilters.client_id = selectedClientId;
      }

      const result = await employeeService.getEmployees(requestFilters);

      if (result.success) {
        setEmployees(result.data || []);
        setFilteredEmployees(result.data || []);
      } else {
        throw new Error(result.error || 'Failed to load employees');
      }

      // Load employee statistics (non-blocking - don't fail if this fails)
      try {
        const statsResult = await employeeService.getEmployeeStats();
        if (statsResult.success) {
          setEmployeeStats(statsResult.data);
        }
      } catch (statsError) {
        console.warn('Failed to load employee statistics:', statsError);
        // Don't throw - stats are optional
      }

    } catch (error) {
      console.error('Employee data loading error:', error);
      const errorMessage = error?.message || error?.error || 'Failed to load employee data. Please check your database connection and try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [selectedClientId]);

  const handleAddEmployee = () => {
    setSelectedEmployee(null);
    setModalMode('add');
    setModalOpen(true);
  };

  // Keyboard shortcuts
  useKeyboardShortcuts({
    'ctrl+r': loadEmployees,
    'ctrl+n': handleAddEmployee,
    'ctrl+f': () => document.querySelector('[data-shortcut="search"]')?.focus()
  });

  // Log activity helper
  const logActivity = async (action, description, entityId = null) => {
    if (user?.id) {
      await activityLogService.logActivity({
        user_id: user.id,
        action,
        description,
        entity_type: 'employee',
        entity_id: entityId
      });
    }
  };

  useEffect(() => {
    let isMounted = true;

    if (user && !authLoading && isMounted) {
      loadEmployees();
    }

    return () => {
      isMounted = false;
    };
  }, [user, authLoading, loadEmployees]);

  useEffect(() => {
    if (user && !authLoading) {
      loadEmployees();
    }
  }, [selectedClientId, user, authLoading, loadEmployees]);

  // Deep-link from Lifecycle dashboard (status filter or open employee)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const status = params.get('status');
    if (status && user && !authLoading) {
      loadEmployees({ status });
    }
  }, [location.search, user, authLoading, loadEmployees]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const employeeId = params.get('employeeId');
    if (!employeeId || employees.length === 0) return;
    const match = employees.find((e) => e.id === employeeId);
    if (match) {
      setSelectedEmployee(match);
      setModalMode('view');
      setModalOpen(true);
    }
  }, [location.search, employees]);

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleFilterChange = async (filters) => {
    await loadEmployees(filters);
  };

  const handleResetFilters = () => {
    setResetKey(prev => prev + 1);
    loadEmployees();
    setSelectedEmployeeIds([]);
  };

  const handleViewEmployee = (employee) => {
    setSelectedEmployee(employee);
    setModalMode('view');
    setModalOpen(true);
  };

  const handleEditEmployee = (employee) => {
    setSelectedEmployee(employee);
    setModalMode('edit');
    setModalOpen(true);
  };

  const handleDeleteEmployee = async (employee) => {
    if (window.confirm(`Are you sure you want to delete ${employee.full_name}?`)) {
      try {
        const result = await employeeService.deleteEmployee(employee.id);

        if (result.success) {
          await loadEmployees(); // Reload data
          await logActivity('DELETE', `Deleted employee: ${employee.full_name}`, employee.id);

          addNotification({
            type: 'success',
            title: 'Employee Deleted',
            message: `${employee.full_name} has been removed from the system.`
          });
        } else {
          throw new Error(result.error);
        }
      } catch (error) {
        addNotification({
          type: 'error',
          title: 'Delete Failed',
          message: error.message || 'Failed to delete employee. Please try again.'
        });
      }
    }
  };

  const handleSaveEmployee = async (employeeData) => {
    try {
      let result;

      if (modalMode === 'add') {
        result = await employeeService.createEmployee(employeeData);
        if (result.success) {
          await logActivity('CREATE', `Added new employee: ${employeeData.full_name}`, result.data.id);
          addNotification({
            type: 'success',
            title: 'Employee Added',
            message: `${employeeData.full_name} has been successfully added.`
          });
        }
      } else if (modalMode === 'edit') {
        result = await employeeService.updateEmployee(selectedEmployee.id, employeeData);
        if (result.success) {
          await logActivity('UPDATE', `Updated employee: ${employeeData.full_name}`, selectedEmployee.id);
          addNotification({
            type: 'success',
            title: 'Employee Updated',
            message: `${employeeData.full_name}'s information has been updated.`
          });
        }
      }

      if (result.success) {
        await loadEmployees(); // Reload data
        setModalOpen(false);
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Save Failed',
        message: error.message || 'Failed to save employee. Please try again.'
      });
    }
  };

  const handleBulkAction = async (actionPayload) => {
    const { action, employeeIds, data } = actionPayload;

    try {
      let result;

      switch (action) {
        case 'department':
          result = await employeeService.bulkUpdateEmployees(employeeIds, { department: data.newDepartment });
          if (result.success) {
            await logActivity('BULK_UPDATE', `Transferred ${employeeIds.length} employees to ${data.newDepartment}`);
            addNotification({
              type: 'success',
              title: 'Department Transfer',
              message: `${employeeIds.length} employees transferred to ${data.newDepartment}.`
            });
          }
          break;

        case 'status':
          result = await employeeService.bulkUpdateEmployees(employeeIds, { status: data.newStatus });
          if (result.success) {
            await logActivity('BULK_UPDATE', `Changed status of ${employeeIds.length} employees to ${data.newStatus}`);
            addNotification({
              type: 'success',
              title: 'Status Updated',
              message: `${employeeIds.length} employees status changed to ${data.newStatus}.`
            });
          }
          break;

        case 'shift':
          result = await employeeService.bulkUpdateEmployees(employeeIds, { shift_id: data.newShiftId });
          if (result.success) {
            await logActivity('BULK_UPDATE', `Rotated ${employeeIds.length} employees to new shift`);
            addNotification({
              type: 'success',
              title: 'Shift Rotated',
              message: `${employeeIds.length} employees assigned to new shift.`
            });
          }
          break;

        case 'email':
          // This would typically integrate with an email service
          await logActivity('BULK_EMAIL', `Sent email "${data.emailSubject}" to ${employeeIds.length} employees`);
          addNotification({
            type: 'success',
            title: 'Email Sent',
            message: `Email "${data.emailSubject}" sent to ${employeeIds.length} employees.`
          });
          result = { success: true };
          break;

        case 'export':
          // This would typically generate and download a file
          await logActivity('EXPORT', `Exported data for ${employeeIds.length} employees`);
          addNotification({
            type: 'success',
            title: 'Export Complete',
            message: `Employee data for ${employeeIds.length} employees exported successfully.`
          });
          result = { success: true };
          break;

        case 'attendance':
          result = await attendanceBatchService.batchCreate({
            employee_ids: employeeIds,
            attendance_date: data.attendanceDate,
            clock_in_time: data.clockInTime,
            clock_out_time: data.clockOutTime,
            status: data.attendanceStatus,
            notes: data.attendanceNotes,
            entry_method: 'manual'
          });
          if (result.success) {
            await logActivity('BULK_ATTENDANCE', `Manual attendance recorded for ${employeeIds.length} employees on ${data.attendanceDate}`);
            addNotification({
              type: 'success',
              title: 'Attendance Recorded',
              message: `Manual attendance recorded for ${employeeIds.length} employees.`
            });
          }
          break;

        default:
          throw new Error('Unknown bulk action');
      }

      if (result.success) {
        await loadEmployees(); // Reload data
        setSelectedEmployeeIds([]);
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Bulk Action Failed',
        message: error.message || 'Failed to perform bulk action. Please try again.'
      });
    }
  };

  const addNotification = (notification) => {
    const newNotification = {
      ...notification,
      id: Date.now(),
      timestamp: new Date().toISOString()
    };
    setNotifications(prev => [newNotification, ...prev]);
  };

  const handleNotificationDismiss = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  // Show loading state during auth initialization
  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Show preview mode for unauthenticated users
  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        {/* Preview Mode Banner */}
        <div className="bg-amber-100 border-b border-amber-200 px-4 py-3">
          <div className="flex items-center justify-center">
            <div className="text-amber-800 text-sm font-medium">
              👥 Preview Mode - Sign in to manage employees
            </div>
          </div>
        </div>

        {/* Rest of component with mock data for preview */}
        {/* ... keep existing mock data rendering logic ... */}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header Navigation */}
      <HeaderNavigation
        onSidebarToggle={handleSidebarToggle}
        user={user}
        userProfile={userProfile}
        notifications={notifications}
      />

      {/* Main Sidebar */}
      <MainSidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      {/* Main Content */}
      <div className={`pt-16 transition-all duration-300 ${isCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
        <div className="p-6">
          {/* Breadcrumb */}
          <BreadcrumbNavigation />

          {/* Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Employee Management</h1>
              <p className="text-muted-foreground">
                Manage your workforce with comprehensive employee administration tools
              </p>
            </div>
            <div className="flex gap-3 mt-4 lg:mt-0">
              <Button
                variant="outline"
                onClick={() => setBulkModalOpen(true)}
                disabled={selectedEmployeeIds.length === 0}
                data-shortcut="bulk-actions"
              >
                Bulk Actions ({selectedEmployeeIds.length})
              </Button>
              <Button
                variant="outline"
                onClick={() => setImportModalOpen(true)}
              >
                Import CSV
              </Button>
              <Button
                variant="outline"
                onClick={() => loadEmployees()}
                data-shortcut="refresh"
              >
                Refresh (F5)
              </Button>
            </div>
          </div>

          {/* Enhanced Employee Management with Tabs */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                <p className="text-muted-foreground text-sm">Loading employees...</p>
              </div>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-3">
                <div className="text-red-600">⚠️</div>
                <div>
                  <p className="text-red-800 font-medium">Unable to load employee data</p>
                  <p className="text-red-600 text-sm">{error}</p>
                  <button
                    onClick={() => loadEmployees()}
                    className="text-red-700 underline text-sm mt-1"
                  >
                    Try again
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <EmployeeManagementTabs
              userProfile={userProfile}
              selectedEmployeeForAttendance={
                selectedEmployeeIds.length === 1
                  ? filteredEmployees.find(emp => emp.id === selectedEmployeeIds[0]) || null
                  : null
              }
            />
          )}

          {/* Stats Cards */}
          <EmployeeStatsCards stats={employeeStats} loading={loading} />

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                <p className="text-muted-foreground text-sm">Loading employees...</p>
              </div>
            </div>
          )}

          {/* Filters and Table */}
          {!loading && !error && (
            <>
              <div className="mb-4 max-w-xs">
                <ClientFilter
                  value={selectedClientId}
                  onChange={setSelectedClientId}
                />
              </div>
              <EmployeeFilters
                key={resetKey}
                onFilterChange={handleFilterChange}
              />

              <EmployeeTable
                onResetFilters={handleResetFilters}
                employees={filteredEmployees}
                onViewEmployee={handleViewEmployee}
                onEditEmployee={handleEditEmployee}
                onDeleteEmployee={handleDeleteEmployee}
                selectedEmployeeIds={selectedEmployeeIds}
                onSelectionChange={setSelectedEmployeeIds}
                userProfile={userProfile}
              />
            </>
          )}
        </div>
      </div>

      {/* Employee Modal */}
      <EmployeeModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        employee={selectedEmployee}
        onSave={handleSaveEmployee}
        mode={modalMode}
      />

      {/* Bulk Actions Modal */}
      <BulkActionsModal
        isOpen={bulkModalOpen}
        onClose={() => setBulkModalOpen(false)}
        selectedEmployees={selectedEmployeeIds}
        onBulkAction={handleBulkAction}
      />

      {/* Import Modal */}
      <EmployeeImportModal
        isOpen={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        onImportSuccess={() => {
          loadEmployees();
          // Optionally keep modal open or close it
        }}
      />

      {/* Notification Toast */}
      <NotificationToast
        notifications={notifications}
        onDismiss={handleNotificationDismiss}
        position="top-right"
      />
    </div>
  );
};

export default EmployeeManagement;